/**
 * useMessageFileUpload
 *
 * Manages the file list selected by the user and delegates the actual S3 upload
 * to uploadFilesV2, surfacing real isUploading / uploadProgress state.
 *
 * @returns {{ files, addFiles, removeFile, clearFiles, upload, isUploading, uploadProgress }}
 */

import { useState, useCallback } from 'react';
import { uploadFilesV2 } from 'utils/filesUtils';

export function useMessageFileUpload() {
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const addFiles = useCallback((dropped) => setFiles((prev) => [...prev, ...dropped]), []);

  const removeFile = useCallback((path) => setFiles((prev) => prev.filter((f) => f.path !== path)), []);

  const clearFiles = useCallback(() => {
    setFiles([]);
    setUploadProgress(0);
  }, []);

  /**
   * Upload all selected files via uploadFilesV2.
   * @param {object} options - forwarded to uploadFilesV2 (caseId, category, message_thread_id, …)
   * @returns {Promise<Array>} uploaded attachment objects
   */
  const upload = useCallback(
    async (options = {}) => {
      if (!files.length) return [];
      setIsUploading(true);
      setUploadProgress(0);
      try {
        return await uploadFilesV2(files, {
          ...options,
          progressCallback: (pct) => setUploadProgress(pct),
        });
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    },
    [files]
  );

  return { files, addFiles, removeFile, clearFiles, upload, isUploading, uploadProgress };
}
